import { useState, useEffect, useCallback } from 'react';
import { Smartphone, Megaphone, CheckCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { ROTULOS_TIPO } from '../components/NotificacaoAvisoPwa';

const PERIODOS = [7, 30, 90];

function formatarDataHora(data) {
  return data ? new Date(data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
}

// Consultas do PWA: avisos dos vendedores e produtos mais consultados (só gerente/admin)
export default function ConsultasPwa() {
  const [avisos, setAvisos] = useState([]);
  const [maisConsultados, setMaisConsultados] = useState([]);
  const [dias, setDias] = useState(30);
  const [carregando, setCarregando] = useState(true);
  const [resolvendo, setResolvendo] = useState(null);

  const carregar = useCallback(async (periodo) => {
    setCarregando(true);
    try {
      const [respAvisos, respConsultas] = await Promise.all([
        apiService.pwa.listarAvisos('aberto'),
        apiService.pwa.maisConsultados(periodo),
      ]);
      setAvisos(respAvisos.data.data ?? []);
      setMaisConsultados(respConsultas.data.data ?? []);
    } catch {
      toast.error('Erro ao carregar dados do PWA.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(dias); }, [carregar, dias]);

  const resolver = async (aviso) => {
    if (!window.confirm(`Marcar o aviso do produto ${aviso.codigo_produto} como resolvido?`)) return;
    setResolvendo(aviso.id);
    try {
      await apiService.pwa.resolverAviso(aviso.id);
      setAvisos(prev => prev.filter(a => a.id !== aviso.id));
      toast.success('Aviso resolvido.');
    } catch {
      toast.error('Erro ao resolver aviso.');
    } finally {
      setResolvendo(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Smartphone className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consultas do PWA</h1>
          <p className="text-sm text-gray-500">Avisos dos vendedores e produtos mais procurados na loja</p>
        </div>
      </div>

      {carregando ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <>
          <section className="bg-white rounded-xl shadow-sm border border-gray-200">
            <h2 className="flex items-center gap-2 px-4 py-3 border-b font-semibold text-gray-800">
              <Megaphone className="w-5 h-5 text-blue-600" />
              Avisos abertos ({avisos.length})
            </h2>
            {avisos.length === 0 ? (
              <p className="px-4 py-8 text-center text-gray-400">Nenhum aviso aberto.</p>
            ) : (
              <ul className="divide-y">
                {avisos.map(a => (
                  <li key={a.id} className="px-4 py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        <span className="text-gray-500">Cód. {a.codigo_produto}</span> — {a.nome_produto}
                      </p>
                      <p className="text-sm text-blue-800">{ROTULOS_TIPO[a.tipo] || a.tipo}</p>
                      {a.observacao && <p className="text-sm text-gray-700">“{a.observacao}”</p>}
                      <p className="text-xs text-gray-400 mt-1">{a.usuario_nome} · {formatarDataHora(a.criado_em)}</p>
                    </div>
                    <button
                      onClick={() => resolver(a)}
                      disabled={resolvendo === a.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Resolvido
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="font-semibold text-gray-800">Mais consultados</h2>
              <div className="flex gap-1">
                {PERIODOS.map(p => (
                  <button
                    key={p}
                    onClick={() => setDias(p)}
                    className={`px-3 py-1 text-sm rounded-lg ${dias === p ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {p} dias
                  </button>
                ))}
              </div>
            </div>
            {maisConsultados.length === 0 ? (
              <p className="px-4 py-8 text-center text-gray-400">Nenhuma consulta no período.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                  <tr>
                    <th className="px-4 py-2">Produto</th>
                    <th className="px-4 py-2 text-right">Consultas</th>
                    <th className="px-4 py-2 text-right">Vendedores</th>
                    <th className="px-4 py-2 text-right">Última</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {maisConsultados.map(m => (
                    <tr key={m.ordem_produto}>
                      <td className="px-4 py-2">
                        <span className="text-gray-500">{m.codigo_produto ? `Cód. ${m.codigo_produto} — ` : `Ordem ${m.ordem_produto} — `}</span>
                        {m.nome_produto || '—'}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{m.consultas}</td>
                      <td className="px-4 py-2 text-right">{m.vendedores}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{formatarDataHora(m.ultima_consulta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
